import { MENTION_TRANSFORMER } from './MentionPlugin/MentionPlugin';
import { TICKET_TRANSFORMER } from './TicketPlugin/TicketPlugin';

export interface MentionReference {
  type: 'mention';
  username: string;
  match: string;
  index: number;
}

export interface TicketReference {
  type: 'ticket';
  ticketId: string;
  showTitle: boolean;
  match: string;
  index: number;
}

export type MarkdownReference = MentionReference | TicketReference;

/**
 * Pure function to extract mention and ticket references from markdown content
 * Uses the same regex patterns as the Lexical transformers for consistency
 */
export function extractMarkdownReferences(
  markdown: string,
): MarkdownReference[] {
  const references: MarkdownReference[] = [];

  // Extract mentions using the same pattern as MENTION_TRANSFORMER
  const mentionRegex = MENTION_TRANSFORMER.importRegExp;
  if (mentionRegex) {
    // Create a new regex with global flag to avoid issues with shared regex state
    const globalMentionRegex = new RegExp(mentionRegex.source, 'g');
    let mentionMatch;
    while ((mentionMatch = globalMentionRegex.exec(markdown)) !== null) {
      references.push({
        type: 'mention',
        username: mentionMatch[1],
        match: mentionMatch[0],
        index: mentionMatch.index,
      });
    }
  }

  // Extract tickets using the same pattern as TICKET_TRANSFORMER
  const ticketRegex = TICKET_TRANSFORMER.importRegExp;
  if (ticketRegex) {
    // Create a new regex with global flag to avoid issues with shared regex state
    const globalTicketRegex = new RegExp(ticketRegex.source, 'g');
    let ticketMatch;
    while ((ticketMatch = globalTicketRegex.exec(markdown)) !== null) {
      references.push({
        type: 'ticket',
        ticketId: ticketMatch[1],
        showTitle: ticketMatch[2] === '+',
        match: ticketMatch[0],
        index: ticketMatch.index,
      });
    }
  }

  // Sort by index to maintain document order
  return references.sort((a, b) => a.index - b.index);
}

/**
 * Extract only mention references from markdown
 */
export function extractMentions(markdown: string): MentionReference[] {
  return extractMarkdownReferences(markdown).filter(
    (ref): ref is MentionReference => ref.type === 'mention',
  );
}

/**
 * Extract only ticket references from markdown
 */
export function extractTickets(markdown: string): TicketReference[] {
  return extractMarkdownReferences(markdown).filter(
    (ref): ref is TicketReference => ref.type === 'ticket',
  );
}

/**
 * Get unique usernames mentioned in markdown
 */
export function getUniqueUsernames(markdown: string): string[] {
  const mentions = extractMentions(markdown);
  return Array.from(new Set(mentions.map((m) => m.username)));
}

/**
 * Get unique ticket IDs referenced in markdown
 */
export function getUniqueTicketIds(markdown: string): string[] {
  const tickets = extractTickets(markdown);
  return Array.from(new Set(tickets.map((t) => t.ticketId)));
}
